/* Draft-only illustration layer. The app remains the owner of seating geometry,
 * furniture visibility, chair positions, capacity and all booking state.
 * Applied to both real SVG instances, including the app's moved dialog view.
 */
if (!customElements.get('asif-spaces-plan-art')) {
  customElements.define('asif-spaces-plan-art', class extends HTMLElement {
    connectedCallback() {
      this.drawings = new Map();
      this.legends = new Map();
      this.abort = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.canvas = this.querySelector('[data-art-canvas]');
      this.scope = this.parentElement;
      this.dialogResize = new ResizeObserver(() => { if (this.dialog.open) this.zoomTo(this.zoom || 1); });
      this.dialogResize.observe(this.querySelector('.plan-art-dialog__scroll'));
      this.scope.addEventListener('click', event => {
        const opener = event.target.closest('[data-art-open]');
        if (opener) this.openDetail(opener);
      }, { signal: this.abort.signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-art-close')) this.dialog.close();
        if (button.hasAttribute('data-art-reset')) this.zoomTo(1);
        if (button.hasAttribute('data-art-zoom')) this.zoomTo(this.zoom + Number(button.dataset.artZoom) * .5);
      }, { signal: this.abort.signal });
      this.dialog.addEventListener('close', () => {
        this.canvas.replaceChildren();
        this.querySelector('[data-art-detail-legend]').replaceChildren();
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, { signal: this.abort.signal });
      this.observer = new MutationObserver(() => this.discover());
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.discover();
    }
    openDetail(opener) {
      const source = opener.closest('.spaces-planner__drawing')?.querySelector('.asf-plan__svg');
      if (!source) return;
      this.opener = opener;
      const copy = source.cloneNode(true);
      copy.setAttribute('viewBox', '-4 -14 145 84');
      const ids = new Map([...copy.querySelectorAll('[id]')].map(node => [node.id, node.id + '-detail']));
      [copy, ...copy.querySelectorAll('*')].forEach(node => {
        for (const attribute of [...node.attributes]) {
          let value = attribute.value;
          if (attribute.name === 'id') value = ids.get(value) || value;
          else {
            value = value.replace(/url\((['\"]?)#([^)'\"]+)\1\)/g, (match, quote, id) => ids.has(id) ? 'url(#' + ids.get(id) + ')' : match);
            if (value.startsWith('#') && ids.has(value.slice(1))) value = '#' + ids.get(value.slice(1));
          }
          if (attribute.name === 'aria-labelledby') value = value.split(' ').map(id => ids.get(id) || id).join(' ');
          node.setAttribute(attribute.name, value);
        }
      });
      copy.removeAttribute('aria-describedby');
      this.canvas.replaceChildren(copy);
      this.querySelector('[data-art-detail-legend]').replaceChildren(this.legends.get(source).cloneNode(true));
      this.dialog.showModal();
      this.zoom = 1;
      this.zoomTo(1);
      this.querySelector('[data-art-close]').focus();
    }
    zoomTo(value) {
      const scroller = this.querySelector('.plan-art-dialog__scroll');
      const oldWidth = this.canvas.getBoundingClientRect().width || scroller.clientWidth;
      const gutter = Math.max(0, (scroller.clientWidth - oldWidth) / 2);
      const centerRatio = (scroller.scrollLeft + scroller.clientWidth / 2 - gutter) / oldWidth;
      this.zoom = Math.max(1, Math.min(3, value));
      const svg = this.canvas.querySelector('svg');
      if (svg) {
        const box = svg.viewBox.baseVal;
        const fitWidth = Math.min(scroller.clientWidth, scroller.clientHeight * box.width / box.height);
        const width = Math.max(1, fitWidth) * this.zoom;
        this.canvas.style.width = width + 'px';
        const scale = width / box.width;
        svg.style.setProperty('--label-u', (((scroller.clientWidth >= 700 ? 14 : 11) + Math.min(2, (this.zoom - 1) * 2)) / scale).toFixed(3));
        svg.classList.toggle('is-eng', scale < 3.4);
        scroller.scrollLeft = this.zoom === 1 ? 0 : centerRatio * width - scroller.clientWidth / 2;
      }
      if (this.zoom === 1) scroller.scrollTop = 0;
      this.querySelector('[data-art-level]').textContent = Math.round(this.zoom * 100) + '%';
      this.querySelector('[data-art-zoom="-1"]').disabled = this.zoom === 1;
      this.querySelector('[data-art-zoom="1"]').disabled = this.zoom === 3;
    }
    discover() {
      for (const [svg, cleanup] of this.drawings) {
        if (!svg.isConnected) { cleanup(); this.drawings.delete(svg); }
        else {
          const legend = this.legends.get(svg);
          if (legend && svg.nextElementSibling !== legend) svg.after(legend);
        }
      }
      document.querySelectorAll('.spaces-planner .asf-plan__svg, [data-asf][data-form-handle="spaces-anfrage"] .asf-plan__svg').forEach(svg => {
        if (this.drawings.has(svg) || svg.dataset.asifArt) return;
        const room = svg.querySelector('.asf-plan__raum');
        const wall = room?.querySelector('.asf-plan__wand');
        if (!room || !wall || !svg.querySelector('.asf-plan__sitze')) return;
        this.decorate(svg, room, wall);
      });
    }
    decorate(svg, room, wall) {
      const ns = 'http://www.w3.org/2000/svg';
      const additions = [];
      const restorations = [];
      const set = (node, name, value) => {
        if (!node) return;
        const previous = node.getAttribute(name);
        node.setAttribute(name, value);
        restorations.push(() => previous === null ? node.removeAttribute(name) : node.setAttribute(name, previous));
      };
      // Owner-corrected kitchen footprint; seating coordinates are untouched.
      svg.querySelectorAll('path[d]').forEach(node => {
        const d = node.getAttribute('d');
        if (d.includes('L48 47L48 58L15.4 58L15.4 47')) set(node, 'd', d.replace('L48 47L48 58L15.4 58L15.4 47', 'L61 47L61 58L25.4 58L25.4 47'));
        if (d === 'M15.4 47L48 47L48 58L15.4 58Z') set(node, 'd', 'M25.4 47L61 47L61 58L25.4 58Z');
      });
      const el = (tag, attributes = {}, parent) => {
        const node = document.createElementNS(ns, tag);
        Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value));
        parent?.append(node);
        return node;
      };
      const groupAfter = (source, extra = {}) => {
        const group = el('g', { class: 'plan-art__detail', 'aria-hidden': 'true', ...extra });
        source.after(group);
        additions.push(group);
        return group;
      };
      const line = (parent, d, extra = {}) => el('path', { d, class: 'plan-art__line', ...extra }, parent);
      const rect = (parent, x, y, width, height, extra = {}) => el('rect', { x, y, width, height, ...extra }, parent);
      const id = (svg.querySelector('title')?.id || 'spaces-plan') + '-art';
      const defs = el('defs');
      additions.push(defs);
      svg.prepend(defs);
      const pattern = (name, width, height, base) => {
        const node = el('pattern', { id: `${id}-${name}`, width, height, patternUnits: 'userSpaceOnUse' }, defs);
        rect(node, 0, 0, width, height, { fill: base });
        return node;
      };
      const timber = pattern('timber', 6, 13, '#b58a5d');
      line(timber, 'M0 0V13M6 0V13M0 6H6M2.4 0C1.2 4 3.5 8 2.2 13M4.3 0C3.6 4 5 7 4.1 13', { stroke: '#866343', 'stroke-width': '.16', opacity: '.65' });
      const concrete = pattern('concrete', 8, 8, '#e9e6dd');
      [[1,2],[6,1],[4,6]].forEach(([cx,cy]) => el('circle', { cx, cy, r: '.1', fill: '#a5a398', opacity: '.5' }, concrete));
      const woven = pattern('woven', 1.3, 1.3, '#d6d1c3');
      line(woven, 'M0 .3H1.3M.3 0V1.3', { stroke: '#ece8df', 'stroke-width': '.13' });
      const props = { '--art-timber': `url(#${id}-timber)`, '--art-concrete': `url(#${id}-concrete)`, '--art-woven': `url(#${id}-woven)` };
      Object.entries(props).forEach(([key,value]) => svg.style.setProperty(key,value));

      // The existing room outline remains the only boundary.
      const shadow = el('path', { d: wall.getAttribute('d'), transform: 'translate(.35 .55)', class: 'plan-art__wall-shadow', 'aria-hidden': 'true' });
      room.prepend(shadow); additions.push(shadow);
      const floor = el('g', { class: 'plan-art__detail plan-art__floor', 'aria-hidden': 'true' });
      wall.before(floor); additions.push(floor);
      rect(floor, 26.5, 6, 28, 40.8, { rx: '.7', class: 'plan-art__rug' });
      rect(floor, 27.1, 6.6, 26.8, 39.6, { rx: '.5', fill: 'none', stroke: '#b9b1a1', 'stroke-width': '.15' });

      // Kitchen worktop and sink are present in the original supplied plan.
      const kitchen = el('g', { class:'plan-art__kitchen' }, floor);
      rect(kitchen, 26.5, 52.2, 28, 4.7, { fill:'#c7b697', stroke:'#747365', 'stroke-width':'.2' });
      line(kitchen, 'M26.5 56H54.5M33.5 52.2V57M40.5 52.2V57M47.5 52.2V57', { stroke:'#858171', 'stroke-width':'.16' });
      rect(kitchen, 37.5, 52.8, 6.6, 3.5, { rx:'.4', fill:'#c7cdc2', stroke:'#77846f', 'stroke-width':'.18' });
      rect(kitchen, 38.1, 53.2, 3.8, 2.6, { rx:'.45', fill:'#8e9d90', stroke:'#647768', 'stroke-width':'.15' });
      line(kitchen, 'M40 52.6V53.7q0 .5 .7 .5M42.6 53.3V55.8M43.2 53.3V55.8', { stroke:'#edf0e7', 'stroke-width':'.2' });
      rect(kitchen, 55, 48.3, 4.7, 8.6, { rx:'.2', fill:'#d3d6cc', stroke:'#7b836e', 'stroke-width':'.2' });
      line(kitchen, 'M55 51.3H59.7M55.7 49.2V50.3M55.7 52V54.4', { stroke:'#737c65', 'stroke-width':'.2' });
      const kitchenLabel = [...svg.querySelectorAll('.asf-plan__labels text')].find(node => node.getAttribute('x') === '31.7');
      set(kitchenLabel, 'x', '40.5');
      set(kitchenLabel, 'dy', '-3');

      // Organic leaves, separate pots and veins replace the three flat circles.
      svg.querySelectorAll('.asf-plan__pflanze').forEach((plant, index) => {
        const group = groupAfter(plant, { transform: `translate(${plant.getAttribute('cx')} ${plant.getAttribute('cy')}) rotate(${index * 27})`, class: 'plan-art__detail plan-art__plant' });
        el('ellipse', { cx: '.45', cy: '.6', rx: '2.4', ry: '1.8', fill: '#252f2222' }, group);
        el('circle', { r: '1.55', fill: '#c19877', stroke: '#8b674d', 'stroke-width': '.2' }, group);
        el('circle', { r: '1.18', fill: '#514237' }, group);
        [0,58,117,178,238,298].forEach((angle, leaf) => {
          const twig = el('g', { transform: `rotate(${angle}) scale(${leaf % 2 ? '.92' : '1'})` }, group);
          el('path', { d: 'M0 .4C-1.55-.15-1.8-2.1-.55-3.05C.2-2.55 1.18-1.2 0 .4Z', fill: ['#486547','#628052','#36583d'][leaf % 3], stroke: '#2e4c33', 'stroke-width': '.13' }, twig);
          line(twig, 'M0 .25Q-.65-1.3-.55-2.65M-.4-1.2l-.65-.3M-.5-1.65l.5-.4', { stroke: '#a3b37a', 'stroke-width': '.12', opacity: '.85' });
        });
        el('path', { d: 'M0 .3C-1.1-.1-.7-1.7.25-2.2C1.1-1.4.8-.2 0 .3', fill: '#819963', stroke: '#4c6944', 'stroke-width': '.13' }, group);
      });

      svg.querySelectorAll('.asf-plan__moebel--tisch').forEach(table => {
        const x = +table.getAttribute('x'), y = +table.getAttribute('y'), w = +table.getAttribute('width'), h = +table.getAttribute('height');
        const group = groupAfter(table, { transform: `translate(${x} ${y})`, 'data-art-furniture': table.dataset.moebel || '' });
        // Each panel is one table, with a quiet solid top and visible frame.
        const panel = (px, py, pw, ph) => {
          rect(group, px-.25, py+.45, pw+.5, ph, { rx:'.15', fill:'#41463d', opacity:'.25' });
          rect(group, px, py, pw, ph, { rx:'.12', fill:'#cbaa7a', stroke:'#756248', 'stroke-width':'.22', class:'plan-art__tabletop' });
          line(group, `M${px+.3} ${py+ph-.4}H${px+pw-.3}`, { stroke:'#a18157', 'stroke-width':'.18' });
          [[px+.35,py+.35],[px+pw-.85,py+.35],[px+.35,py+ph-.85],[px+pw-.85,py+ph-.85]].forEach(([lx,ly]) => rect(group,lx,ly,.5,.5,{fill:'#484a40'}));
        };
        if (table.dataset.moebel === 'blocktisch') {
          for (let i=0;i<4;i++) panel(0,i*h/4,w,h/4-.12);
        } else if (table.dataset.moebel === 'buehnentisch') {
          panel(0,1.8,w,w); panel(0,h-w-1.8,w,w);
        } else panel(0,0,w,h);
      });
      const sofa = svg.querySelector('.asf-plan__moebel--sofa');
      if (sofa) {
        const g = groupAfter(sofa, { transform: `translate(${sofa.getAttribute('x')} ${sofa.getAttribute('y')})` });
        rect(g, .3, .25, 25.4, 1.5, { rx: '.6', fill: '#6e7768', stroke: '#55604f', 'stroke-width': '.18' });
        [2.1,9.5,16.9].forEach(x => rect(g, x, 2, 7, 4.8, { rx: '.7', fill: '#a2a894', stroke: '#64735c', 'stroke-width': '.16' }));
        [0,24.3].forEach(x => rect(g, x, 1.1, 1.7, 5.8, { rx: '.6', fill: '#7f8975' }));
        rect(g, 3, 2.1, 2.8, 2.8, { rx: '.25', fill: '#d1ad54', transform: 'rotate(14 4.4 3.5)', stroke: '#ac8945', 'stroke-width': '.15' });
        rect(g, 19, 2.2, 2.6, 2.6, { rx: '.25', fill: '#b97c60', transform: 'rotate(-12 20.3 3.5)', stroke: '#965f49', 'stroke-width': '.15' });
      }
      const bank = svg.querySelector('.asf-plan__moebel--bank');
      if (bank) {
        const g = groupAfter(bank, { transform: `translate(${bank.getAttribute('x')} ${bank.getAttribute('y')})`, 'data-art-furniture': 'bank' });
        rect(g, .15, .2, .7, 14.6, { rx: '.25', fill: '#68725c' });
        [0.5,5.1,9.7].forEach(y => rect(g, 1, y, 3.1, 4.3, { rx: '.4', fill: '#a0a68b', stroke: '#69765b', 'stroke-width': '.16' }));
      }
      const pouf = svg.querySelector('.asf-plan__moebel--pouf');
      if (pouf) {
        const g = groupAfter(pouf, { transform: `translate(${pouf.getAttribute('cx')} ${pouf.getAttribute('cy')})` });
        el('circle', { r: 2.05, fill: '#b99a7e', stroke: '#8e725c', 'stroke-width': '.16' }, g);
        line(g, 'M-1.45-1.45 1.45 1.45M1.45-1.45-1.45 1.45', { stroke: '#d5bca3', 'stroke-width': '.12' });
        el('circle', { r: '.25', fill: '#8e725c' }, g);
      }
      const bar = svg.querySelector('.asf-plan__moebel--bar');
      if (bar) {
        const g = groupAfter(bar, { transform: `translate(${bar.getAttribute('x')} ${bar.getAttribute('y')})`, class: 'plan-art__detail plan-art__coffee' });
        // A compact overhead espresso machine: steel body, drip tray,
        // two groups and portafilter handles facing the service counter.
        rect(g, .8, 5.3, 4.6, 7.8, { rx: '.4', fill: '#c4c5bc', stroke: '#414940', 'stroke-width': '.22' });
        rect(g, 1.1, 5.7, 2.7, 7, { rx: '.2', fill: '#e1e0d5' });
        rect(g, 4, 5.7, 1, 7, { fill: '#51574f' });
        [7.5,10.7].forEach(y => {
          el('circle', { cx: 4.4, cy: y, r: '.55', fill: '#d1cfc1', stroke: '#343d33', 'stroke-width': '.14' }, g);
          line(g, `M4.7 ${y}H6.4`, { stroke: '#343d33', 'stroke-width': '.38' });
        });
        [6.6,8.5,10.4].forEach(y => el('circle', { cx: 2.1, cy: y, r: '.45', fill: '#f6f3e8', stroke: '#7e8577', 'stroke-width': '.12' }, g));
        // Grinder and a neat tray of glassware on the existing back bar.
        rect(g, 1.1, 15.3, 3.7, 3.6, { rx: '.25', fill: '#454d40' });
        el('circle', { cx: 2.9, cy: 16.7, r: '1.15', fill: '#765b42', stroke: '#bdb5a3', 'stroke-width': '.2' }, g);
        rect(g, .9, 22, 4.7, 5.4, { rx: '.2', fill: '#72664e' });
        for (let row=0;row<3;row++) for(let col=0;col<2;col++) el('circle', { cx: 2 + col*2.1, cy: 23 + row*1.6, r: '.5', fill: '#e1dfcb', stroke: '#a2ae98', 'stroke-width': '.12' }, g);
      }
      const counter = svg.querySelector('.asf-plan__moebel--tresen');
      if (counter) {
        const g = groupAfter(counter, { transform: `translate(${counter.getAttribute('x')} ${counter.getAttribute('y')})` });
        line(g, 'M.4 .4V25.6M2.8 .4V25.6', { stroke: '#edc89b', 'stroke-width': '.2' });
        line(g, 'M3.05 .2V25.8', { stroke:'#514a3b', 'stroke-width':'.35' });
        [6,18].forEach(y => {
          el('circle', { cx: 1.6, cy: y, r: '.85', fill: '#f3eee0', stroke: '#9e8868', 'stroke-width': '.14' }, g);
          el('circle', { cx: 1.6, cy: y, r: '.43', fill: '#67422c' }, g);
          line(g, `M2.1 ${y-.2}q.6 0 .3 .55`, { stroke: '#e4d8c2', 'stroke-width': '.15' });
        });
      }
      const shelf = svg.querySelector('.asf-plan__moebel--regal');
      if (shelf) {
        const g = groupAfter(shelf, { transform: `translate(${shelf.getAttribute('x')} ${shelf.getAttribute('y')})` });
        // The original drawn plan and room film show two decks here.
        [2.6,10].forEach(x => {
          rect(g, x-2.15, .3, 4.3, 4.4, { rx: '.2', fill: '#727568', stroke: '#3c4437', 'stroke-width': '.15' });
          el('circle', { cx:x, cy:2.5, r:1.72, fill:'#343d33' }, g);
          el('circle', { cx:x, cy:2.5, r:1.35, fill:'none', stroke:'#89937d', 'stroke-width':'.12' }, g);
          el('circle', { cx:x, cy:2.5, r:.48, fill:'#c4a25b' }, g);
          line(g, `M${x+1.65} 1L${x+1.5} 2.2 ${x+.9} 2.6`, { stroke:'#ded8c4', 'stroke-width':'.16' });
        });
        rect(g, 5, .4, 2.6, 4.2, { rx:'.2', fill:'#444b3e' });
        [5.5,6.3,7.1].forEach(x => {
          line(g, `M${x} 1.6V3.7`, { stroke:'#d5ceb9', 'stroke-width':'.13' });
          rect(g, x-.2, 2.3, .4, .45, { fill:'#d5ceb9' });
          el('circle', { cx:x, cy:1, r:.22, fill:'#bab9a3' }, g);
        });
      }
      const glass = svg.querySelector('.asf-plan__linie--glas');
      if (glass) {
        const g = groupAfter(glass);
        line(g, 'M15.7 0V47M16.3 0V47', { stroke: '#536454', 'stroke-width': '.2' });
        for(let y=0;y<=47;y+=5.8) line(g, `M15.5 ${y}H16.5`, { stroke: '#354536', 'stroke-width': '.3' });
      }
      // Fixed empty stools explain the furniture; occupied seats retain app state.
      const stools = el('g', { class:'plan-art__detail plan-art__stools', 'aria-hidden':'true' });
      svg.querySelector('.asf-plan__sitze').before(stools); additions.push(stools);
      const stoolPositions = [[60.6,12.5],[60.6,19.5],[60.6,26.5],[80.6,8.5],[80.6,14.5],[80.6,20.5],[80.6,26.5]];
      const stool = (g, occupied = false) => {
        line(g, 'M-1.4-1.4L1.4 1.4M1.4-1.4L-1.4 1.4', { stroke:'#454a41', 'stroke-width':'.35' });
        el('circle',{r:'1.8',fill:occupied?'#d8bc4e':'#c29a69',stroke:'#555344','stroke-width':'.2'},g);
        el('circle',{r:'1.4',fill:'none',stroke:occupied?'#ead681':'#dbc19b','stroke-width':'.14'},g);
      };
      stoolPositions.forEach(([x,y]) => stool(el('g',{transform:`translate(${x} ${y})`},stools)));
      const seats = [...svg.querySelectorAll('.asf-plan__sitz')];
      seats.forEach(seat => {
        const furniture = el('g', { class:'plan-art__stool-seat', 'aria-hidden':'true' },seat);
        stool(furniture,true); additions.push(furniture);
        const g = el('g', { class: 'plan-art__occupant', 'aria-hidden': 'true' }, seat);
        additions.push(g);
        el('path', { d: 'M-1.1.6Q0-.05 1.1.6L.85 1Q0 .6-.85 1Z', fill: '#fbf4d7' }, g);
        el('circle', { cx: 0, cy: '-.25', r: '.66', fill: '#fbf4d7', stroke: '#8c7739', 'stroke-width': '.1' }, g);
      });
      const updateSeats = () => {
        const title = svg.querySelector('title');
        if (title?.textContent.includes('steht ein 4K-Bildschirm')) title.textContent = title.textContent.replace('steht ein 4K-Bildschirm', 'hängt ein 56-Zoll-4K-Bildschirm an der Wand');
        seats.forEach(seat => {
        const match = seat.style.transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px/);
        seat.classList.toggle('plan-art__is-stool', !!match && stoolPositions.some(([x,y]) => Math.abs(x-Number(match[1]))<.1 && Math.abs(y-Number(match[2]))<.1));
      });
      };
      const seatObserver = new MutationObserver(updateSeats);
      seats.forEach(seat => seatObserver.observe(seat,{attributes:true,attributeFilter:['style']}));
      const planTitle = svg.querySelector('title');
      if (planTitle) seatObserver.observe(planTitle, { childList:true, characterData:true, subtree:true });
      updateSeats();
      const monitor = svg.querySelector('.asf-plan__moebel--monitor');
      set(monitor,'x','96.5'); set(monitor,'y','45.7'); set(monitor,'width','14'); set(monitor,'height','1.1');
      const monitorLabel = [...svg.querySelectorAll('text')].find(node => node.textContent === '4K-Screen');
      if (monitorLabel) {
        const label = el('text', {class:monitorLabel.getAttribute('class'),x:'103.5',y:'42.5','text-anchor':'middle'},monitorLabel.parentElement);
        label.textContent = '56″ · 4K · Wand'; additions.push(label); set(monitorLabel,'visibility','hidden');
      }
      // Area keys sit beyond the perimeter. Names stay at a readable HTML size
      // outside the drawing, including the form's moved mobile SVG.
      set(svg, 'viewBox', '-4 -14 145 84');
      // Native fit() and area focus both replace the camera. Add annotation
      // space to each native camera once, retaining its focused area.
      let framedView = '-4 -14 145 84';
      const keepAnnotationMargin = () => {
        const value = svg.getAttribute('viewBox');
        if (value === framedView) return;
        const box = value?.trim().split(/[\s,]+/).map(Number);
        if (!box || box.length !== 4 || !box.every(Number.isFinite) || box[2] <= 0 || box[3] <= 0) return;
        framedView = value === '-3 -3 130 64' ? '-4 -14 145 84' : `${box[0]-4} ${box[1]-12} ${box[2]+20} ${box[3]+24}`;
        svg.setAttribute('viewBox', framedView);
      };
      const framingObserver = new MutationObserver(keepAnnotationMargin);
      framingObserver.observe(svg, { attributes:true, attributeFilter:['viewBox'] });
      svg.querySelectorAll('.asf-plan__labels text:not(.asf-plan__label--technik)').forEach(label => set(label, 'data-art-area-label', 'true'));
      const annotations = el('g', { class:'plan-art__area-keys', 'aria-hidden':'true' }, svg);
      additions.push(annotations);
      const key = (number, x, y) => {
        const g = el('g', { transform:`translate(${x} ${y})` }, annotations);
        el('circle', { class:'plan-art__area-key-disc', r:'2.5' }, g);
        el('text', { class:'plan-art__area-key-number', 'text-anchor':'middle', 'dominant-baseline':'central' }, g).textContent = number;
      };
      [[1,0,16,8],[2,17,67,42],[3,68,75,71.5],[4,76,124,100]].forEach(([n,start,end,center]) => {
        line(annotations, `M${start} -1V-3H${end}V-1`, { class:'plan-art__area-bracket' });
        key(n,center,-8.5);
      });
      // Keep the observed wide gate opening; jambs and an outside bracket
      // communicate its extent without an arrow through the lounge furniture.
      line(annotations, 'M123 4.5H126M123 42H126M126 4.5H129V42H126', { class:'plan-art__area-bracket' });
      line(annotations, 'M124 4.5V42', { class:'plan-art__gate-threshold' });
      key(5,135,23.25);
      [[6,25.4,61,43.2],[7,78,90,84]].forEach(([n,start,end,center]) => {
        line(annotations, `M${start} 59V61H${end}V59`, { class:'plan-art__area-bracket' });
        key(n,center,66);
      });
      const legend = this.querySelector('[data-art-area-legend]').content.firstElementChild.cloneNode(true);
      svg.after(legend);
      this.legends.set(svg, legend);
      svg.dataset.asifArt = '1';
      const button = svg.closest('.spaces-planner__drawing')?.querySelector('[data-art-open]');
      if (button) button.hidden = false;
      this.drawings.set(svg, () => {
        seatObserver.disconnect();
        framingObserver.disconnect();
        seats.forEach(seat => seat.classList.remove('plan-art__is-stool'));
        additions.forEach(node => node.remove());
        legend.remove();
        this.legends.delete(svg);
        restorations.reverse().forEach(restore => restore());
        Object.keys(props).forEach(key => svg.style.removeProperty(key));
        delete svg.dataset.asifArt;

      });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.dialogResize?.disconnect();
      this.abort?.abort();
      this.dialog?.close();
      this.drawings?.forEach(cleanup => cleanup());
      this.drawings?.clear();
    }
  });
}
